import {NAME_MAX_LENGTH} from "../public/js/utils/identity.js";
import {DeckBuilder} from "./DeckBuilder.jsx";

// The lobby asks one question at a time. First: are you starting a game or joining
// one? Only then the things that answer actually needs — a mode and a name for the
// creator, a code for the joiner. Everything on one screen made a player read past
// four fields to find the one button they wanted.
//
// The step lives on the controller, not here, so the choice survives a re-render (a
// poll landing, the account loading) and stays under test.

function LookPicker({controller}) {
  const look = controller.appearanceChoices();

  return (
    <fieldset className="look-picker">
      <legend>Din figur</legend>
      <div className="icon-grid" role="radiogroup" aria-label="Välj figur">
        {look.icons.map((icon) => (
          <button
            key={icon.id}
            type="button"
            role="radio"
            aria-checked={icon.selected}
            aria-label={icon.label}
            title={icon.label}
            className={icon.selected ? "icon-swatch selected" : "icon-swatch"}
            onClick={() => controller.chooseIcon(icon.id)}
          >
            <span aria-hidden="true">{icon.symbol}</span>
          </button>
        ))}
      </div>
      <div className="color-grid" role="radiogroup" aria-label="Välj färg">
        {look.colors.map((color) => (
          <button
            key={color.id}
            type="button"
            role="radio"
            aria-checked={color.selected}
            aria-label={color.label}
            title={color.label}
            className={color.selected ? "color-swatch selected" : "color-swatch"}
            style={{"--swatch": color.hex}}
            onClick={() => controller.chooseColor(color.id)}
          />
        ))}
      </div>
    </fieldset>
  );
}

function NameField({panel, savedName, autoFocus = false}) {
  // A signed-in player plays under the account name, so the field goes away rather
  // than offering a second, conflicting identity. joinGame ignores a typed name for
  // them too, so nothing depends on this branch alone.
  if (panel.name) return null;
  return (
    <label>
      Namn
      <input
        name="name"
        autoComplete="nickname"
        placeholder="Skriv ditt namn"
        maxLength={NAME_MAX_LENGTH}
        defaultValue={savedName}
        autoFocus={autoFocus}
      />
    </label>
  );
}

export function Lobby({controller, snapshot}) {
  const {lobbyMessage, savedName, urlGameCode, lobbyStep} = snapshot;
  const panel = controller.accountBlock();
  const builder = controller.deckBuilder();
  const reconnect = controller.reconnectOffer();

  // The builder takes the lobby's place while it is open, hero and all: on a phone it is
  // the whole screen. Closing it brings the lobby back on the step it was on.
  if (builder) return <DeckBuilder controller={controller} view={builder} />;

  return (
    <div className="lobby-card">
      <p className="eyebrow">Vardagsrumsduell</p>
      <h1>SipRoyale</h1>
      <p className="intro">
        {lobbyMessage || "Skapa ett spel och dela koden, eller gå med i någon annans."}
      </p>

      {/* Signing in is optional and never blocks play, but it is the first thing the
          lobby offers: it saves your statistics and carries your name between devices.
          The whole block stays hidden unless the server says the auth service is
          configured, so a laptop running without CAS shows the lobby as it always was. */}
      <section className={panel.authenticated ? "account-panel signed-in" : "account-panel"} hidden={!panel.available}>
        <div className="account-identity">
          <p className="account-title">{panel.title}</p>
          <p className="account-subtitle">{panel.subtitle}</p>
        </div>
        {panel.authenticated ? (
          <button className="plain-button" type="button" onClick={() => controller.endLogin()}>
            Logga ut
          </button>
        ) : (
          <button className="primary-button account-login" type="button" onClick={() => controller.startLogin()}>
            Logga in med Mutari auth
          </button>
        )}
        {/* Back into the lobby or match the server still has this account seated in —
            from this phone or a new one. */}
        {reconnect && (
          <div className="account-reconnect">
            <span>{reconnect.detail}</span>
            <button className="primary-button" type="button" onClick={() => controller.reconnect()}>
              {reconnect.label}
            </button>
          </div>
        )}
        {/* A signed-in player's own deck: what they will be dealt, and the way in. */}
        {panel.authenticated && (
          <div className="account-deck">
            <span>{panel.deckStatus}</span>
            <button className="plain-button" type="button" onClick={() => controller.openDeckBuilder()}>
              Bygg kortlek
            </button>
          </div>
        )}
      </section>

      <LobbySteps controller={controller} panel={panel} savedName={savedName} urlGameCode={urlGameCode} lobbyStep={lobbyStep} />
    </div>
  );
}

function LobbySteps({controller, panel, savedName, urlGameCode, lobbyStep}) {
  const history = controller.accountGames();

  function onJoin(event) {
    event.preventDefault();
    const data = new FormData(event.target);
    controller.joinGame(data.get("code"), data.get("name"));
  }

  function onCreate(event, mode) {
    controller.createGame({mode, name: new FormData(event.currentTarget.form).get("name")});
  }

  return (
    <>
      {lobbyStep === "start" && (
        <div className="start-choice">
          <button className="big-choice create" type="button" onClick={() => controller.goToCreate()}>
            <span className="big-choice-title">Skapa spel</span>
            <span className="big-choice-sub">Välj läge och dela koden</span>
          </button>
          <button className="big-choice join" type="button" onClick={() => controller.goToJoin()}>
            <span className="big-choice-title">Gå med</span>
            <span className="big-choice-sub">Du behöver bara koden</span>
          </button>
        </div>
      )}

      {lobbyStep === "create" && (
        <form className="join-form" onSubmit={(event) => event.preventDefault()}>
          <button className="step-back" type="button" onClick={() => controller.goBack()}>
            ← Tillbaka
          </button>
          <NameField panel={panel} savedName={savedName} />
          <LookPicker controller={controller} />
          <p className="step-label">Välj läge</p>
          {/* One button per mode, each carrying its own explanation: picking the game
              and starting it is one tap rather than a radio group plus a submit. */}
          {controller.gameModeChoices().map((mode) => (
            <button
              key={mode.id}
              className="mode-choice"
              type="button"
              onClick={(event) => onCreate(event, mode.id)}
            >
              <span className="mode-choice-title">{mode.label}</span>
              <span className="mode-choice-sub">{mode.blurb}</span>
            </button>
          ))}
        </form>
      )}

      {lobbyStep === "join" && (
        <form className="join-form" onSubmit={onJoin}>
          <button className="step-back" type="button" onClick={() => controller.goBack()}>
            ← Tillbaka
          </button>
          <label>
            Spelkod
            {/* A share link has already filled the code in, so the name is what is left. */}
            <input name="code" maxLength={5} placeholder="ABCDE" defaultValue={urlGameCode} autoFocus={!urlGameCode} />
          </label>
          <NameField panel={panel} savedName={savedName} autoFocus={Boolean(urlGameCode)} />
          <LookPicker controller={controller} />
          <button className="primary-button" type="submit">Gå med</button>
        </form>
      )}

      {/* History only exists for a signed-in player — a guest has nothing to show
          and gets the lobby exactly as it was. Rows come shaped from the controller. */}
      {panel.authenticated && lobbyStep === "start" && (
        <section className="account-history" aria-label="Dina senaste spel">
          <p className="eyebrow">Dina senaste spel</p>
          {history.length === 0 ? (
            <p className="account-empty">Inga färdigspelade matcher än.</p>
          ) : (
            <ul className="history-list">
              {history.map((game) => (
                <li key={game.key} className={game.won ? "history-row won" : "history-row"}>
                  <span className="history-code">{game.code}</span>
                  <span className="history-outcome">{game.outcome}</span>
                  <span className="history-detail">{game.detail}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
